// @ts-nocheck
import { MaximizedPlayerManager } from './maximized-player';
import { ZoomedPlayerManager } from './zoomed-player';
import { createTwitchPlayerUrl, getTwitchParentDomains } from '../../services/twitch-embed';
import { t } from '../../services/i18n';

let twitchPlayerSdkPromise = null;
// Chromium 145/146 and Twitch background heuristics may suspend muted media.
// Community reports consistently identify 1% as the reliable floor; the UI
// still exposes these background players as logically muted.
const BACKGROUND_KEEPALIVE_VOLUME = 0.01;

function loadTwitchPlayerSdk() {
    if (window.Twitch?.Player) return Promise.resolve(window.Twitch);
    if (twitchPlayerSdkPromise) return twitchPlayerSdkPromise;

    twitchPlayerSdkPromise = new Promise((resolve, reject) => {
        const existingScript = document.querySelector('script[data-twitch-player-sdk]');
        const script = existingScript || document.createElement('script');
        const timeoutId = window.setTimeout(
            () => reject(new Error('Délai de chargement de l’API Twitch Player dépassé.')),
            8000,
        );

        const handleLoad = () => {
            window.clearTimeout(timeoutId);
            window.Twitch?.Player
                ? resolve(window.Twitch)
                : reject(new Error('API Twitch Player indisponible après son chargement.'));
        };
        const handleError = () => {
            window.clearTimeout(timeoutId);
            reject(new Error('Impossible de charger l’API Twitch Player.'));
        };

        script.addEventListener('load', handleLoad, { once: true });
        script.addEventListener('error', handleError, { once: true });

        if (!existingScript) {
            script.src = 'https://player.twitch.tv/js/embed/v1.js';
            script.async = true;
            script.dataset.twitchPlayerSdk = 'true';
            document.head.appendChild(script);
        }
    }).catch((error) => {
        twitchPlayerSdkPromise = null;
        throw error;
    });

    return twitchPlayerSdkPromise;
}

/**
 * PlayerManager - Optimized for modern browsers with performance as top priority
 * Loads Twitch embeds through a bounded queue and preserves their DOM instances.
 */
export class PlayerManager {
    constructor(onPlayerRemoved = null) {
        this.players = new Map(); // Map of player elements by streamer ID
        this.container = document.getElementById('streams-container');
        this.layout = 'auto';
        this.onPlayerRemoved = onPlayerRemoved;
        this.loadingPlayers = new Set(); // Track players being added
        this.removingPlayers = new Set();
        this.twitchPlayers = new Map(); // Instances de l'API Twitch, avec iframe de secours
        this.playerLoadQueue = [];
        this.queuedPlayerIds = new Set();
        this.loadingEmbedIds = new Set();
        this.activePlayerLoads = 0;
        this.playbackRetryTimers = new Map();
        this.playbackRetryAttempts = new Map();
        this.audioActivationTimers = new Map();
        this.offlinePlayerIds = new Set();
        this.playbackHealthTimer = null;
        const reducedCapacity = navigator.connection?.saveData || navigator.hardwareConcurrency <= 4;
        this.maxConcurrentPlayerLoads = reducedCapacity ? 2 : 4;
        this.playerLoadDelay = 100;
        this.playerVisibilityObserver = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                const streamerId = entry.target.dataset.streamerId;
                if (!streamerId) return;
                const player = this.players.get(streamerId);
                if (!player) return;
                player.isNearViewport = entry.isIntersecting && entry.intersectionRatio >= 0.05;
                if (player.isNearViewport) this.resumePlayer(streamerId, true);
            });
        }, {
            root: null,
            rootMargin: '100px 0px',
            threshold: [0, 0.05],
        });
        this.handlePageWake = () => {
            if (document.visibilityState === 'hidden') {
                this.players.forEach((player) => {
                    player.isNearViewport = false;
                });
                return;
            }
            this.players.forEach((_player, streamerId) => {
                this.resumePlayer(streamerId, true);
            });
        };
        this.handleUserPlaybackGesture = () => {
            this.players.forEach((_player, streamerId) => this.resumePlayer(streamerId, true));
        };
        document.addEventListener('visibilitychange', this.handlePageWake);
        document.addEventListener('pointerdown', this.handleUserPlaybackGesture, { capture: true });
        document.addEventListener('keydown', this.handleUserPlaybackGesture, { capture: true });
        window.addEventListener('focus', this.handlePageWake);
        window.addEventListener('pageshow', this.handlePageWake);
        
        // Pre-load critical CSS styles
        this.injectCriticalStyles();
        
        // Enable hardware acceleration for animations
        this.container.style.transform = 'translateZ(0)';
        this.container.style.willChange = 'transform';

        // Comme dans l'ancienne application, charger le SDK avant le premier
        // ajout évite que tous les embeds attendent le téléchargement du script.
        void loadTwitchPlayerSdk().catch((error) => {
            console.warn('Préchargement de l’API Twitch Player impossible :', error);
        });
        
        // Initialize specialized managers
        this.maximizedManager = new MaximizedPlayerManager(this);
        this.zoomedManager = new ZoomedPlayerManager(this);
    }
    
    /**
     * Inject critical CSS styles to prevent layout shifts
     */
    injectCriticalStyles() {
        if (document.getElementById('player-critical-styles')) return;
        
        const styleSheet = document.createElement('style');
        styleSheet.id = 'player-critical-styles';
        styleSheet.textContent = `
            .player-container {
                position: relative;
                overflow: hidden;
                background-color: #0e0e10;
                transition: transform 0.3s ease, box-shadow 0.3s ease;
                transform: translateZ(0);
            }
            .player-placeholder {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                display: flex;
                align-items: center;
                justify-content: center;
                flex-direction: column;
                background-color: rgba(0, 0, 0, 0.7);
                z-index: 2;
            }
            .player-iframe {
                width: 100%;
                height: 100%;
                border: none;
                opacity: 1;
            }
            .player-reordered {
                box-shadow: 0 0 0 2px var(--primary-color);
                z-index: 5;
            }
            .player-loading .spinner {
                width: 40px;
                height: 40px;
                border: 3px solid rgba(100, 65, 165, 0.3);
                border-radius: 50%;
                border-top-color: var(--primary-color);
                animation: spin 1s linear infinite;
            }
            @keyframes spin {
                to { transform: rotate(360deg); }
            }
        `;
        
        document.head.appendChild(styleSheet);
    }
    
    enqueuePlayerLoad(streamerId, streamerName, placeholder) {
        if (this.twitchPlayers.has(streamerId) || this.queuedPlayerIds.has(streamerId) ||
            this.loadingEmbedIds.has(streamerId)) return;

        this.queuedPlayerIds.add(streamerId);
        this.playerLoadQueue.push({ streamerId, streamerName, placeholder });
        this.processPlayerLoadQueue();
    }

    processPlayerLoadQueue() {
        while (this.activePlayerLoads < this.maxConcurrentPlayerLoads && this.playerLoadQueue.length > 0) {
            const task = this.playerLoadQueue.shift();
            this.queuedPlayerIds.delete(task.streamerId);

            const player = this.players.get(task.streamerId);
            if (!player || !task.placeholder.isConnected || player.element !== task.placeholder.parentNode) continue;

            this.activePlayerLoads += 1;
            this.loadingEmbedIds.add(task.streamerId);

            void this.loadPlayerIframe(task.streamerId, task.streamerName, task.placeholder)
                .catch((error) => console.error('Erreur de chargement du player Twitch :', error))
                .finally(() => {
                    this.activePlayerLoads -= 1;
                    this.loadingEmbedIds.delete(task.streamerId);
                    window.setTimeout(() => this.processPlayerLoadQueue(), this.playerLoadDelay);
                });
        }
    }

    /**
     * Update the channel of an existing player without recreating the iframe
     */
    updatePlayerChannel(streamerId, newStreamerName) {
        const player = this.players.get(streamerId);
        const twitchPlayer = this.twitchPlayers.get(streamerId);

        if (!player || player.name === newStreamerName) return;

        player.name = newStreamerName;
        const placeholder = player.element.querySelector('.player-placeholder');
        if (placeholder) placeholder.dataset.streamerName = newStreamerName;

        if (typeof twitchPlayer?.setChannel === 'function') {
            twitchPlayer.setChannel(newStreamerName);
        } else if (twitchPlayer instanceof HTMLIFrameElement) {
            twitchPlayer.src = this.createTwitchPlayerUrl(newStreamerName);
        }
    }

    createTwitchPlayerUrl(streamerName) {
        return createTwitchPlayerUrl(streamerName);
    }

    /**
     * Create the official Twitch iframe once for this streamer.
     */
    async loadPlayerIframe(streamerId, streamerName, placeholder) {
        if (this.twitchPlayers.has(streamerId)) return;

        const container = placeholder.parentNode;
        const playerData = this.players.get(streamerId);
        if (!container || !playerData || playerData.element !== container) return;
        
        // Prevent layout shifts
        container.style.aspectRatio = '16/9';
        
        const playerHost = document.createElement('div');
        playerHost.id = `twitch-player-${streamerId}`;
        playerHost.className = 'player-embed-host';
        container.appendChild(playerHost);

        try {
            const twitch = await loadTwitchPlayerSdk();
            if (!playerHost.isConnected || this.players.get(streamerId) !== playerData) return;

            // Twitch requires the embed to be visible and unobscured to start.
            // Do not leave the loading overlay above the iframe while it boots.
            placeholder.style.display = 'none';
            playerHost.classList.add('loaded');

            const twitchPlayer = new twitch.Player(playerHost.id, {
                width: '100%',
                height: '100%',
                channel: playerData.name || streamerName,
                parent: [...getTwitchParentDomains()],
                autoplay: true,
                muted: true,
            });
            this.twitchPlayers.set(streamerId, twitchPlayer);
            playerData.volumeControlAvailable = true;
            twitchPlayer.addEventListener(twitch.Player.READY, () => {
                const currentPlayer = this.players.get(streamerId);
                if (currentPlayer && this.twitchPlayers.get(streamerId) === twitchPlayer) {
                    // Let muted autoplay begin before switching to the keepalive
                    // transport state, otherwise browser autoplay policy may block it.
                    twitchPlayer.setVolume(BACKGROUND_KEEPALIVE_VOLUME);
                    twitchPlayer.setMuted(true);
                    this.syncVolumeControls(streamerId);
                    const previousTimer = this.audioActivationTimers.get(streamerId);
                    if (previousTimer) window.clearTimeout(previousTimer);
                    const activationTimer = window.setTimeout(() => {
                        this.audioActivationTimers.delete(streamerId);
                        if (this.twitchPlayers.get(streamerId) !== twitchPlayer) return;
                        this.applyPlayerAudioState(streamerId);
                        this.resumePlayer(streamerId, true);
                    }, 500);
                    this.audioActivationTimers.set(streamerId, activationTimer);
                    if (this.isPlayerNearViewport(currentPlayer.element)) {
                        this.resumePlayer(streamerId, true);
                    }
                }
            });
            twitchPlayer.addEventListener(twitch.Player.PLAYING, () => {
                if (this.twitchPlayers.get(streamerId) === twitchPlayer) {
                    this.cancelPlaybackRetry(streamerId);
                    this.applyPlayerAudioState(streamerId);
                }
            });
            twitchPlayer.addEventListener(twitch.Player.PAUSE, () => {
                if (this.twitchPlayers.get(streamerId) === twitchPlayer) {
                    this.resumePlayer(streamerId, true);
                }
            });
            twitchPlayer.addEventListener(twitch.Player.PLAYBACK_BLOCKED, () => {
                if (this.twitchPlayers.get(streamerId) === twitchPlayer) {
                    this.resumePlayer(streamerId, true);
                }
            });
            twitchPlayer.addEventListener(twitch.Player.OFFLINE, () => {
                this.offlinePlayerIds.add(streamerId);
                this.cancelPlaybackRetry(streamerId);
            });
            twitchPlayer.addEventListener(twitch.Player.ONLINE, () => {
                this.offlinePlayerIds.delete(streamerId);
                this.resumePlayer(streamerId, true);
            });
        } catch (error) {
            console.warn('API interactive Twitch indisponible, retour au player iframe :', error);
            if (!playerHost.isConnected) return;
            if (this.twitchPlayers.get(streamerId)) this.twitchPlayers.delete(streamerId);
            playerHost.replaceChildren();

            const iframe = document.createElement('iframe');
            iframe.className = 'player-iframe';
            iframe.src = this.createTwitchPlayerUrl(playerData.name || streamerName);
            iframe.title = `Twitch stream: ${playerData.name || streamerName}`;
            iframe.allow = 'autoplay; fullscreen; picture-in-picture';
            iframe.allowFullscreen = true;
            iframe.loading = 'eager';
            placeholder.style.display = 'none';
            playerHost.classList.add('loaded');
            this.twitchPlayers.set(streamerId, iframe);
            playerData.volumeControlAvailable = false;
            this.syncVolumeControls(streamerId);

            iframe.addEventListener('load', () => {
                iframe.classList.add('loaded');
            }, { once: true });
            iframe.addEventListener('error', () => {
                placeholder.replaceChildren(document.createTextNode('Impossible de charger le player Twitch.'));
            }, { once: true });
            playerHost.appendChild(iframe);
        }
    }

    isPlayerNearViewport(element) {
        const rect = element.getBoundingClientRect();
        return rect.bottom >= -100 && rect.top <= window.innerHeight + 100;
    }

    resumePlayer(streamerId, force = false) {
        const twitchPlayer = this.twitchPlayers.get(streamerId);
        if (typeof twitchPlayer?.play !== 'function' || this.offlinePlayerIds.has(streamerId)) return;

        let paused = true;
        try {
            if (typeof twitchPlayer.isPaused === 'function') paused = twitchPlayer.isPaused();
        } catch {
            paused = true;
        }
        if (!force && !paused) {
            this.cancelPlaybackRetry(streamerId);
            return;
        }

        try {
            twitchPlayer.play();
        } catch {
            // The retry below will run after Twitch finishes waking the iframe.
        }

        this.schedulePlaybackRetry(streamerId);
    }

    schedulePlaybackRetry(streamerId) {
        if (this.playbackRetryTimers.has(streamerId) || this.offlinePlayerIds.has(streamerId)) return;
        const attempt = this.playbackRetryAttempts.get(streamerId) || 0;
        const delays = [250, 750, 1500, 3000, 5000];
        const timer = window.setTimeout(() => {
            this.playbackRetryTimers.delete(streamerId);
            this.playbackRetryAttempts.set(streamerId, attempt + 1);
            this.resumePlayer(streamerId);
        }, delays[Math.min(attempt, delays.length - 1)]);
        this.playbackRetryTimers.set(streamerId, timer);
    }

    cancelPlaybackRetry(streamerId) {
        const timer = this.playbackRetryTimers.get(streamerId);
        if (timer) window.clearTimeout(timer);
        this.playbackRetryTimers.delete(streamerId);
        this.playbackRetryAttempts.delete(streamerId);
    }

    startPlaybackHealthCheck() {
        if (this.playbackHealthTimer) return;
        this.playbackHealthTimer = window.setInterval(() => {
            this.players.forEach((_player, streamerId) => this.resumePlayer(streamerId));
        }, 2000);
    }

    stopPlaybackHealthCheckIfIdle() {
        if (this.players.size > 0 || !this.playbackHealthTimer) return;
        window.clearInterval(this.playbackHealthTimer);
        this.playbackHealthTimer = null;
    }

    syncAllPlayerPerformance() {
        this.players.forEach((player, streamerId) => {
            player.isNearViewport = this.isPlayerNearViewport(player.element);
            if (player.isNearViewport || player.zoomed || player.maximized) {
                this.resumePlayer(streamerId, true);
            }
        });
    }

    getPlayerVolumeState(streamerId) {
        const player = this.players.get(streamerId);
        if (!player) return { volume: 0, muted: true, available: false };

        const twitchPlayer = this.twitchPlayers.get(streamerId);
        try {
            // A logically muted player deliberately remains technically
            // unmuted at BACKGROUND_KEEPALIVE_VOLUME (see applyPlayerAudioState).
            // Reading that transport state back would corrupt the UI state.
            if (!player.muted && typeof twitchPlayer?.getVolume === 'function') {
                player.volume = twitchPlayer.getVolume();
            }
        } catch {
            // Le player peut être présent mais pas encore prêt : conserver l'état demandé.
        }

        return {
            volume: player.volume,
            muted: player.muted,
            available: player.volumeControlAvailable !== false,
        };
    }

    applyPlayerAudioState(streamerId) {
        const player = this.players.get(streamerId);
        const twitchPlayer = this.twitchPlayers.get(streamerId);
        if (!player || typeof twitchPlayer?.setVolume !== 'function') return;

        const transportVolume = player.muted
            ? BACKGROUND_KEEPALIVE_VOLUME
            : Math.max(BACKGROUND_KEEPALIVE_VOLUME, player.volume);

        try {
            twitchPlayer.setVolume(transportVolume);
            // Deliberately avoid Twitch's muted transport state: recent Chromium
            // versions suspend muted Twitch media outside the foreground.
            twitchPlayer.setMuted(false);
        } catch {
            // READY will apply the requested state once Twitch accepts commands.
        }
    }

    setPlayerVolume(streamerId, volume) {
        const player = this.players.get(streamerId);
        if (!player) return null;

        player.volume = Math.min(1, Math.max(0, Number(volume) || 0));
        player.muted = player.volume === 0;

        this.applyPlayerAudioState(streamerId);

        this.syncVolumeControls(streamerId);
        return this.getPlayerVolumeState(streamerId);
    }

    adjustPlayerVolume(streamerId, delta) {
        const player = this.players.get(streamerId);
        if (!player) return null;
        const currentState = this.getPlayerVolumeState(streamerId);
        return this.setPlayerVolume(streamerId, currentState.volume + delta);
    }

    togglePlayerMuted(streamerId) {
        const player = this.players.get(streamerId);
        if (!player) return null;

        player.muted = !this.getPlayerVolumeState(streamerId).muted;
        this.applyPlayerAudioState(streamerId);
        this.syncVolumeControls(streamerId);
        return this.getPlayerVolumeState(streamerId);
    }

    focusAudioOnPlayer(streamerId) {
        if (!this.players.has(streamerId)) return;

        this.players.forEach((player, id) => {
            const isTarget = id === streamerId;

            // Un volume nul ne produit aucun son même une fois le player démuté.
            if (isTarget && player.volume <= BACKGROUND_KEEPALIVE_VOLUME) player.volume = 0.5;
            player.muted = !isTarget;

            this.applyPlayerAudioState(id);

            this.syncVolumeControls(id);
        });
    }

    syncVolumeControls(streamerId) {
        const player = this.players.get(streamerId);
        if (!player) return;

        const percentage = Math.round(player.volume * 100);
        document.querySelectorAll(`[data-volume-streamer-id="${streamerId}"]`).forEach((control) => {
            control.disabled = player.volumeControlAvailable === false;
            if (control.matches('input[type="range"]')) control.value = String(percentage);
            if (control.classList.contains('volume-value')) {
                control.textContent = player.muted ? t('audio.muted') : `${percentage}%`;
            }
            if (control.classList.contains('volume-toggle')) {
                const icon = control.querySelector('i');
                if (icon) icon.className = player.muted || percentage === 0
                    ? 'fas fa-volume-mute'
                    : percentage < 50 ? 'fas fa-volume-down' : 'fas fa-volume-up';
                control.title = player.muted ? t('audio.unmute') : t('audio.mute');
            }
        });
    }

    syncPlayerModeControls(streamerId) {
        const player = this.players.get(streamerId);
        if (!player) return;

        const streamerItem = document.querySelector(`.streamer-item[data-streamer-id="${streamerId}"]`);
        const focusButton = streamerItem?.querySelector('.focus-btn');
        const maximizeButton = streamerItem?.querySelector('.maximize-btn');

        if (focusButton) {
            focusButton.classList.toggle('is-active', player.zoomed);
            focusButton.title = player.zoomed ? t('stream.leaveFocus') : t('stream.focus');
            const icon = focusButton.querySelector('i');
            if (icon) icon.className = player.zoomed ? 'fas fa-search-minus' : 'fas fa-search-plus';
        }

        if (maximizeButton) {
            maximizeButton.classList.toggle('is-active', player.maximized);
            maximizeButton.title = player.maximized ? t('stream.restore') : t('stream.maximize');
            const icon = maximizeButton.querySelector('i');
            if (icon) icon.className = player.maximized ? 'fas fa-compress' : 'fas fa-expand';
        }
    }

    /**
     * Add a new player with optimized loading
     */
    addPlayer(streamerId, streamerName, displayName = streamerName, profileImageUrl = '') {
        // Skip if already exists or loading
        if (this.players.has(streamerId) || this.loadingPlayers.has(streamerId)) {
            // If player exists but streamer name changed, update the channel
            const existingPlayer = this.players.get(streamerId);
            if (existingPlayer && existingPlayer.name !== streamerName) {
                this.updatePlayerChannel(streamerId, streamerName);
            }
            this.syncVolumeControls(streamerId);
            this.syncPlayerModeControls(streamerId);
            return;
        }

        this.loadingPlayers.add(streamerId);

        // Create container with all content
        const playerContainer = document.createElement('div');
        playerContainer.className = 'player-container';
        playerContainer.id = `player-${streamerId}`;
        playerContainer.dataset.streamerId = streamerId;
        
        // Add controls
        const controls = document.createElement('div');
        controls.className = 'player-controls';
        
        const zoomBtn = document.createElement('button');
        zoomBtn.innerHTML = '<i class="fas fa-search-plus"></i>';
        zoomBtn.setAttribute('aria-label', t('stream.focus'));
        zoomBtn.setAttribute('title', t('stream.focus'));
        zoomBtn.addEventListener('click', () => this.zoomedManager.zoomPlayer(streamerId));
        
        const maximizeBtn = document.createElement('button');
        maximizeBtn.innerHTML = '<i class="fas fa-expand"></i>';
        maximizeBtn.setAttribute('aria-label', t('stream.maximize'));
        maximizeBtn.setAttribute('title', t('stream.maximize'));
        maximizeBtn.addEventListener('click', () => this.maximizedManager.maximizePlayer(streamerId));
        
        const removeBtn = document.createElement('button');
        removeBtn.innerHTML = '<i class="fas fa-times"></i>';
        removeBtn.setAttribute('aria-label', t('common.remove'));
        removeBtn.setAttribute('title', t('common.remove'));
        removeBtn.addEventListener('click', () => this.removePlayer(streamerId));

        const volumeDownBtn = document.createElement('button');
        volumeDownBtn.innerHTML = '<i class="fas fa-minus"></i>';
        volumeDownBtn.setAttribute('aria-label', t('audio.lower'));
        volumeDownBtn.setAttribute('title', 'Volume −10 %');
        volumeDownBtn.dataset.volumeStreamerId = streamerId;
        volumeDownBtn.addEventListener('click', () => this.adjustPlayerVolume(streamerId, -0.1));

        const muteBtn = document.createElement('button');
        muteBtn.className = 'volume-toggle';
        muteBtn.dataset.volumeStreamerId = streamerId;
        muteBtn.innerHTML = '<i class="fas fa-volume-mute"></i>';
        muteBtn.setAttribute('aria-label', 'Activer ou couper le son');
        muteBtn.setAttribute('title', t('audio.unmute'));
        muteBtn.addEventListener('click', () => this.togglePlayerMuted(streamerId));

        const volumeUpBtn = document.createElement('button');
        volumeUpBtn.innerHTML = '<i class="fas fa-plus"></i>';
        volumeUpBtn.setAttribute('aria-label', t('audio.raise'));
        volumeUpBtn.setAttribute('title', 'Volume +10 %');
        volumeUpBtn.dataset.volumeStreamerId = streamerId;
        volumeUpBtn.addEventListener('click', () => this.adjustPlayerVolume(streamerId, 0.1));
        
        controls.appendChild(zoomBtn);
        controls.appendChild(maximizeBtn);
        controls.appendChild(volumeDownBtn);
        controls.appendChild(muteBtn);
        controls.appendChild(volumeUpBtn);
        controls.appendChild(removeBtn);
        playerContainer.appendChild(controls);
        
        // Create placeholder for lazy loading
        const placeholder = document.createElement('div');
        placeholder.className = 'player-placeholder';
        placeholder.dataset.streamerId = streamerId;
        placeholder.dataset.streamerName = streamerName;

        const identity = document.createElement('div');
        identity.className = 'player-placeholder-identity';

        const avatarWrapper = document.createElement('div');
        avatarWrapper.className = 'player-placeholder-avatar-wrapper';

        const avatarFallback = document.createElement('span');
        avatarFallback.className = 'player-placeholder-avatar-fallback';
        avatarFallback.textContent = displayName.trim().charAt(0).toUpperCase() || '?';
        avatarWrapper.appendChild(avatarFallback);

        if (profileImageUrl) {
            const avatar = document.createElement('img');
            avatar.className = 'player-placeholder-avatar';
            avatar.src = profileImageUrl;
            avatar.alt = '';
            avatar.decoding = 'async';
            avatar.addEventListener('error', () => avatar.remove(), { once: true });
            avatarWrapper.appendChild(avatar);
        }

        const streamerDisplayName = document.createElement('div');
        streamerDisplayName.className = 'player-placeholder-name';
        streamerDisplayName.textContent = displayName;

        identity.append(avatarWrapper, streamerDisplayName);
        placeholder.appendChild(identity);
        
        playerContainer.appendChild(placeholder);
        
        // Add to DOM
        this.container.appendChild(playerContainer);
        
        // Add to players map
        this.players.set(streamerId, {
            element: playerContainer,
            name: streamerName,
            displayName,
            profileImageUrl,
            maximized: false,
            zoomed: false,
            volume: BACKGROUND_KEEPALIVE_VOLUME,
            muted: false,
            volumeControlAvailable: true,
            isNearViewport: this.isPlayerNearViewport(playerContainer),
        });
        this.syncVolumeControls(streamerId);
        this.playerVisibilityObserver.observe(playerContainer);
        this.startPlaybackHealthCheck();
        
        // Remove from loading set
        this.loadingPlayers.delete(streamerId);
        
        // Update layout after player is added
        this.updateLayout();

        // Load every selected stream, including players below the viewport. The
        // bounded queue still prevents all Twitch embeds from starting at once.
        this.enqueuePlayerLoad(streamerId, streamerName, placeholder);
        
        // Notify latency manager if available
        if (window.app && window.app.latencyManager) {
            window.app.latencyManager.onPlayerAdded(streamerId);
        }
    }

    refreshTranslations() {
        this.players.forEach((player, streamerId) => {
            const controls = player.element.querySelector('.player-controls');
            const buttons = controls?.querySelectorAll('button');
            if (buttons?.[0]) {
                buttons[0].title = player.zoomed ? t('stream.leaveFocus') : t('stream.focus');
                buttons[0].setAttribute('aria-label', buttons[0].title);
            }
            if (buttons?.[1]) {
                buttons[1].title = player.maximized ? t('stream.restore') : t('stream.maximize');
                buttons[1].setAttribute('aria-label', buttons[1].title);
            }
            if (buttons?.[2]) buttons[2].setAttribute('aria-label', t('audio.lower'));
            if (buttons?.[4]) buttons[4].setAttribute('aria-label', t('audio.raise'));
            if (buttons?.[5]) {
                buttons[5].title = t('common.remove');
                buttons[5].setAttribute('aria-label', buttons[5].title);
            }
            this.syncVolumeControls(streamerId);
        });
    }

    /**
     * Remove a player with smooth animation
     */
    removePlayer(streamerId) {
        const player = this.players.get(streamerId);
        if (!player || this.removingPlayers.has(streamerId)) return;

        if (player.maximized) this.disableMaximizedMode(streamerId);
        if (player.zoomed) this.disableZoomMode(streamerId);

        this.removingPlayers.add(streamerId);
        this.playerVisibilityObserver.unobserve(player.element);
        this.cancelPlaybackRetry(streamerId);
        this.offlinePlayerIds.delete(streamerId);
        const audioActivationTimer = this.audioActivationTimers.get(streamerId);
        if (audioActivationTimer) window.clearTimeout(audioActivationTimer);
        this.audioActivationTimers.delete(streamerId);
        this.playerLoadQueue = this.playerLoadQueue.filter(task => task.streamerId !== streamerId);
        this.queuedPlayerIds.delete(streamerId);
        // Removing the iframe is the complete cleanup for a direct embed.
        const twitchPlayer = this.twitchPlayers.get(streamerId);
        player.element.querySelector('.player-embed-host')?.remove();
        if (twitchPlayer instanceof HTMLIFrameElement) twitchPlayer.remove();
        this.twitchPlayers.delete(streamerId);
        
        player.element.classList.add('player-removing');
        player.element.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
        player.element.style.opacity = '0';
        player.element.style.transform = 'scale(0.95)';
        
        setTimeout(() => {
            player.element.remove();
            this.players.delete(streamerId);
            this.removingPlayers.delete(streamerId);
            this.stopPlaybackHealthCheckIfIdle();
            this.updateLayout();
            
            // Notify latency manager if available
            if (window.app && window.app.latencyManager) {
                window.app.latencyManager.onPlayerRemoved(streamerId);
            }
            
            if (this.onPlayerRemoved) {
                this.onPlayerRemoved(streamerId);
            }
        }, 300);
    }

    /**
     * Update the layout of players
     */
    updateLayout(layout = null) {
        if (layout) {
            this.layout = layout;
        }
        
        // Check if any player is zoomed or maximized
        let hasZoomedPlayer = false;
        let hasMaximizedPlayer = false;
        this.players.forEach(p => {
            if (p.zoomed) hasZoomedPlayer = true;
            if (p.maximized) hasMaximizedPlayer = true;
        });
        
        // If we have a zoomed or maximized player, don't change the layout
        if (hasZoomedPlayer || hasMaximizedPlayer) return;
        
        // Remove zoom-active class if no player is zoomed
        this.container.classList.remove('zoom-active');
        
        // Reset order properties on all players when updating layout
        this.players.forEach(player => {
            player.element.style.order = '';
        });
        
        // Nombre de players
        const playerCount = this.players.size;
        
        // Use CSS style application in a single operation
        requestAnimationFrame(() => {
            // Reset container styles first
            Object.assign(this.container.style, {
                display: 'grid',
                gap: '10px',
                width: '100%',
                height: 'auto', // Auto pour permettre l'expansion verticale
                minHeight: '0',
                maxHeight: 'none',
                overflowY: 'visible',
                alignContent: 'start', // Aligner au début pour permettre l'expansion
                alignItems: 'start',
                justifyContent: 'stretch',
                gridAutoFlow: 'dense' // Always use dense to respect order properties
            });
            
            // Reset all player styles
            this.players.forEach(player => {
                Object.assign(player.element.style, {
                    gridColumn: "",
                    gridRow: "",
                    display: "",
                    zIndex: "",
                    width: "100%",
                    height: "100%",
                    minHeight: "0",
                    aspectRatio: "16/9"
                    // Keep order property as is for reordering
                });
            });
            
            // Get parent element to adjust container height
            const contentElement = this.container.parentElement;
            if (contentElement) {
                contentElement.style.overflowY = 'auto';
                contentElement.style.display = 'flex';
                contentElement.style.flexDirection = 'column';
            }
            
            // Layout adaptatif basé sur le nombre de players
            if (this.layout === 'auto' || !this.layout) {
                if (playerCount === 1) {
                    // Un seul player prend tout l'espace
                    this.container.style.gridTemplateColumns = '1fr';
                } else if (playerCount === 2) {
                    // Deux players côte à côte
                    this.container.style.gridTemplateColumns = '1fr 1fr';
                } else if (playerCount === 3) {
                    // Un grand player en haut, deux plus petits en bas
                    this.container.style.gridTemplateColumns = 'repeat(2, 1fr)';
                    
                    // Premier player prend toute la largeur
                    let firstPlayer = true;
                    this.players.forEach(player => {
                        if (firstPlayer) {
                            player.element.style.gridColumn = '1 / span 2';
                            firstPlayer = false;
                        }
                    });
                } else if (playerCount === 4) {
                    // Grille 2x2
                    this.container.style.gridTemplateColumns = 'repeat(2, 1fr)';
                } else if (playerCount <= 6) {
                    // Grille 3x2
                    this.container.style.gridTemplateColumns = 'repeat(3, 1fr)';
                } else if (playerCount <= 9) {
                    // Grille 3x3
                    this.container.style.gridTemplateColumns = 'repeat(3, 1fr)';
                } else {
                    // Pour plus de 9 players: maintenir la grille à 3 colonnes
                    // Garder la même taille de player qu'avec 9 players et ajouter en dessous
                    this.container.style.gridTemplateColumns = 'repeat(3, 1fr)';
                    this.container.style.gridAutoRows = 'min-content';
                }
                
                // Toujours utiliser gridAutoFlow dense pour combler les espaces
                this.container.style.gridAutoFlow = 'row dense';
                
                // Si on a plus de 9 players, s'assurer que le défilement vertical est activé
                if (playerCount > 9 && contentElement) {
                    contentElement.style.overflowY = 'auto';
                }
            } else {
                // Utiliser le layout spécifié par l'utilisateur
                switch (this.layout) {
                    case '2x2':
                        Object.assign(this.container.style, {
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gridAutoFlow: 'row dense'
                        });
                        break;
                    case '3x3':
                        Object.assign(this.container.style, {
                            gridTemplateColumns: 'repeat(3, 1fr)',
                            gridAutoFlow: 'row dense'
                        });
                        break;
                    case '1x2':
                        Object.assign(this.container.style, {
                            gridTemplateColumns: 'repeat(2, 1fr)',
                            gridAutoFlow: 'row dense'
                        });
                        break;
                        case '2x1':
                            Object.assign(this.container.style, {
                                gridTemplateColumns: '1fr',
                                gridAutoFlow: 'row dense'
                            });
                            break;
                    }
                }
                
                // S'assurer que tous les players ont le bon aspect ratio
                this.players.forEach(player => {
                    player.element.style.aspectRatio = '16/9';
                    player.element.style.minHeight = '0';
                });
                
                // Force layout recalculation
                this.container.offsetHeight;
            });
        }
        
        /**
         * Méthode pour désactiver le zoom d'un player 
         * (à utiliser lors de la transition entre modes)
         */
        disableZoomMode(streamerId) {
            const player = this.players.get(streamerId);
            if (!player || !player.zoomed) return;
            
            // Désactiver le mode zoom
            player.zoomed = false;
            
            // Réinitialiser les styles du container
            this.container.classList.remove('zoom-active');
            
            // Réinitialiser les styles de tous les players
            this.players.forEach((p, id) => {
                p.element.classList.remove('player-zoomed', 'player-below-zoomed');
                p.element.style.order = '';
                
            });
            
            // Restaurer l'icône du bouton zoom
            const zoomButton = player.element.querySelector('.player-controls button:first-child i');
            if (zoomButton) {
                zoomButton.className = 'fas fa-search-plus';
            }
            this.syncPlayerModeControls(streamerId);
            this.syncAllPlayerPerformance();
        }

        /**
         * Méthode pour désactiver le mode maximisé d'un player
         * (à utiliser lors de la transition entre modes)
         */
        disableMaximizedMode(streamerId) {
            const player = this.players.get(streamerId);
            if (!player || !player.maximized) return;
            
            // Désactiver le mode maximisé
            player.maximized = false;
            player.element.classList.remove('player-maximized');
            this.container.classList.remove('maximized-active');
            this.container.parentElement?.classList.remove('player-maximized-active');
            
            // Réinitialiser les styles du player
            player.element.style.gridColumn = '';
            player.element.style.gridRow = '';
            player.element.style.zIndex = '';
            
            // Réinitialiser l'affichage des autres players
            this.players.forEach((p, id) => {
                p.element.classList.remove('player-maximized-background');
                if (id !== streamerId) {
                    p.element.style.display = '';
                }
            });
            
            // Restaurer l'icône du bouton maximize
            const maxButton = player.element.querySelector('.player-controls button:nth-child(2) i');
            if (maxButton) {
                maxButton.className = 'fas fa-expand';
            }
            this.syncPlayerModeControls(streamerId);
            this.syncAllPlayerPerformance();
        }
        

        /**
         * Reorder players without detaching them from DOM
         */
        reorderPlayers(newOrder) {
            if (!newOrder?.length || this.players.size <= 1) return;
            
            // Fast validation
            const validIds = newOrder.filter(id => this.players.has(id));
            if (validIds.length <= 1) return;
            
            // Check if we have a zoomed player
            let zoomedPlayerId = null;
            this.players.forEach((player, id) => {
                if (player.zoomed) {
                    zoomedPlayerId = id;
                }
            });
            
            // Get current positions
            const currentPositions = new Map();
            Array.from(this.container.children).forEach((child, index) => {
                const id = child.dataset?.streamerId;
                if (id && this.players.has(id)) {
                    currentPositions.set(id, index);
                }
            });
            
            // Check if order is different
            let needsReordering = false;
            const currentOrder = Array.from(this.container.children)
                .map(child => child.dataset?.streamerId)
                .filter(id => id && this.players.has(id));
            
            if (validIds.length !== currentOrder.length) {
                needsReordering = true;
            } else {
                for (let i = 0; i < validIds.length; i++) {
                    if (validIds[i] !== currentOrder[i]) {
                        needsReordering = true;
                        break;
                    }
                }
            }
            
            if (!needsReordering) return;
            
            // Use CSS Grid order property instead of rearranging DOM
            // This preserves Twitch player instances
            const elements = this.container.children;
            const animationTargets = [];
            
            // First reset any previous order
            for (let i = 0; i < elements.length; i++) {
                if (!zoomedPlayerId || elements[i].dataset?.streamerId !== zoomedPlayerId) {
                    elements[i].style.order = "initial";
                }
            }
            
            // Apply new order using CSS order property
            validIds.forEach((id, newIndex) => {
                const playerData = this.players.get(id);
                if (!playerData) return;
                
                const element = playerData.element;
                
                // If this is the zoomed player, keep its order as -1 to ensure it stays on top
                // Otherwise, apply order based on new position
                if (id === zoomedPlayerId) {
                    element.style.order = "-1";
                } else if (zoomedPlayerId) {
                    // If any player is zoomed, start order at 1 for non-zoomed players
                    element.style.order = newIndex + 1;
                } else {
                    element.style.order = newIndex;
                }
                
                // Mark for animation if position changed significantly
                const oldIndex = currentPositions.get(id);
                if (oldIndex !== undefined && Math.abs(oldIndex - newIndex) > 0) {
                    animationTargets.push(element);
                }
            });
            
            // Keep grid-auto-flow dense to ensure all spaces are filled
            this.container.style.gridAutoFlow = "dense";
            
            // In zoomed mode, ensure the container has proper columns for other players
            if (zoomedPlayerId) {
                this.container.style.gridTemplateColumns = 'repeat(5, 1fr)';
            }
            
            // Animate the moved elements
            if (animationTargets.length) {
                requestAnimationFrame(() => {
                    // Add animation class
                    animationTargets.forEach(el => el.classList.add('player-reordered'));
                    
                    // Remove class after animation completes
                    setTimeout(() => {
                        animationTargets.forEach(el => el.classList.remove('player-reordered'));
                    }, 500);
                });
            }
            
            // Update layout in next frame - but preserve order properties and zoom state
            requestAnimationFrame(() => {
                if (zoomedPlayerId) {
                    // If a player is zoomed, just ensure proper layout
                    // Don't call updateLayoutGridOnly as it might reset important zoom styles
                    this.ensureZoomedLayout(zoomedPlayerId);
                } else {
                    // Only update grid settings without moving DOM elements
                    this.updateLayoutGridOnly(this.layout);
                }
            });
        }

        /**
         * Ensure proper layout for zoomed mode after reordering
         */
        ensureZoomedLayout(zoomedPlayerId) {
            // Make sure the zoomed player is properly styled
            const zoomedPlayer = this.players.get(zoomedPlayerId);
            if (!zoomedPlayer) return;
            
            // Re-apply zoomed styles
            zoomedPlayer.element.classList.add('player-zoomed');
            zoomedPlayer.element.style.order = '-1';
            
            // Ensure all other players are below zoomed
            this.players.forEach((player, id) => {
                if (id !== zoomedPlayerId) {
                    player.element.classList.add('player-below-zoomed');
                }
            });
            
            // Ensure container has proper styles
            this.container.classList.add('zoom-active');
            this.container.style.gridTemplateColumns = 'repeat(5, 1fr)';
            this.container.style.gridAutoRows = 'min-content';
            this.container.style.gridAutoFlow = 'row dense';
        }

        /**
         * Update only grid layout settings without affecting children
         */
        updateLayoutGridOnly(layout = null) {
            if (layout) {
                this.layout = layout;
            }
            
            // Check if any player is zoomed
            let hasZoomedPlayer = false;
            this.players.forEach(p => {
                if (p.zoomed) hasZoomedPlayer = true;
            });
            
            // If we have a zoomed player, don't change the layout
            if (hasZoomedPlayer) return;
            
            // Remove zoom-active class if no player is zoomed
            this.container.classList.remove('zoom-active');
            
            // Nombre de players
            const playerCount = this.players.size;
            
            requestAnimationFrame(() => {
                // Reset container styles first, but keep grid-auto-flow dense
                Object.assign(this.container.style, {
                    display: 'grid',
                    gap: '10px',
                    width: '100%',
                    height: 'auto',
                    minHeight: '0',
                    maxHeight: 'none',
                    overflowY: 'visible',
                    alignContent: 'start',
                    alignItems: 'start',
                    justifyContent: 'stretch',
                    gridAutoFlow: 'dense' // Keep this to respect order properties
                });
                
                // Get parent element to adjust container height
                const contentElement = this.container.parentElement;
                if (contentElement) {
                    contentElement.style.overflowY = 'auto';
                    contentElement.style.display = 'flex';
                    contentElement.style.flexDirection = 'column';
                }
                
                // Layout adaptatif basé sur le nombre de players
                if (this.layout === 'auto' || !this.layout) {
                    if (playerCount === 1) {
                        this.container.style.gridTemplateColumns = '1fr';
                    } else if (playerCount === 2) {
                        this.container.style.gridTemplateColumns = '1fr 1fr';
                    } else if (playerCount === 3) {
                        this.container.style.gridTemplateColumns = 'repeat(2, 1fr)';
                    } else if (playerCount === 4) {
                        this.container.style.gridTemplateColumns = 'repeat(2, 1fr)';
                    } else if (playerCount <= 6) {
                        this.container.style.gridTemplateColumns = 'repeat(3, 1fr)';
                    } else if (playerCount <= 9) {
                        this.container.style.gridTemplateColumns = 'repeat(3, 1fr)';
                    } else {
                        this.container.style.gridTemplateColumns = 'repeat(3, 1fr)';
                        this.container.style.gridAutoRows = 'min-content';
                    }
                    
                    // Si on a plus de 9 players, s'assurer que le défilement vertical est activé
                    if (playerCount > 9 && contentElement) {
                        contentElement.style.overflowY = 'auto';
                    }
                } else {
                    // Utiliser le layout spécifié par l'utilisateur
                    switch (this.layout) {
                        case '2x2':
                            this.container.style.gridTemplateColumns = 'repeat(2, 1fr)';
                            break;
                        case '3x3':
                            this.container.style.gridTemplateColumns = 'repeat(3, 1fr)';
                            break;
                        case '1x2':
                            this.container.style.gridTemplateColumns = 'repeat(2, 1fr)';
                            break;
                        case '2x1':
                            this.container.style.gridTemplateColumns = '1fr';
                            break;
                    }
                }
                
                // Force layout recalculation
                this.container.offsetHeight;
            });
        }
    }
    
